import { useState } from "react";
import { Eye, Pause, Play, RefreshCw, Search } from "lucide-react";
import { api } from "../lib/api";
import { formatDate, truncate } from "../lib/format";
import type { LogEntry } from "../lib/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusBadge } from "../components/Ui";

export default function LogsPage() {
  const [level, setLevel] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(true);
  const [selected, setSelected] = useState<LogEntry | null>(null);
  const { data, loading, error, refresh } = useResource(
    (signal) => api.logs({ limit: 300, level: level || undefined, q: search || undefined }, signal),
    [level, search],
    live ? 5_000 : undefined,
  );
  const logs = data?.items || [];

  return (
    <div className="page-stack">
      <PageHeader title="系统日志" description="已脱敏的客户端、管理端与上游请求记录" actions={<><button className={`button ${live ? "button-live" : "button-secondary"}`} onClick={() => setLive((value) => !value)}>{live ? <Pause size={16} /> : <Play size={16} />}{live ? "暂停实时" : "恢复实时"}</button><button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button></>} />
      <div className="filter-bar">
        <form className="search-box" onSubmit={(event) => { event.preventDefault(); setSearch(query.trim()); }}><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="路径、任务或请求内容" aria-label="搜索日志" /></form>
        <select value={level} onChange={(event) => setLevel(event.target.value)} aria-label="日志级别"><option value="">全部级别</option><option value="DEBUG">DEBUG</option><option value="INFO">INFO</option><option value="WARNING">WARNING</option><option value="ERROR">ERROR</option></select>
      </div>
      <section className="panel panel-flush log-panel">
        {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : !logs.length ? <EmptyState label="没有匹配的日志" /> : (
          <div className="table-wrap">
            <table className="logs-table"><thead><tr><th>时间</th><th>级别</th><th>事件</th><th>消息</th><th>关联</th><th /></tr></thead><tbody>{logs.map((log) => (
              <tr key={log.id}>
                <td className="nowrap">{formatDate(log.created_at)}</td>
                <td><StatusBadge value={log.level || "INFO"} /></td>
                <td><code>{log.event || "event"}</code></td>
                <td title={log.message}>{truncate(log.message, 96)}</td>
                <td><div className="primary-cell"><code>{log.task_id ? truncate(log.task_id, 16) : "-"}</code><small>{log.worker_id || (log.account_id ? `account ${log.account_id}` : "")}</small></div></td>
                <td className="actions-cell"><button className="icon-button" onClick={() => setSelected(log)} title="查看上下文" aria-label="查看上下文"><Eye size={17} /></button></td>
              </tr>
            ))}</tbody></table>
          </div>
        )}
      </section>
      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title="日志上下文" width="large">
        {selected && <div className="detail-stack"><div className="detail-grid"><div className="detail-item"><span>时间</span><strong>{formatDate(selected.created_at)}</strong></div><div className="detail-item"><span>级别</span><StatusBadge value={selected.level} /></div><div className="detail-item"><span>事件</span><code>{selected.event || "-"}</code></div><div className="detail-item"><span>任务</span><code>{selected.task_id || "-"}</code></div></div><div><h3 className="detail-heading">消息</h3><pre className="code-block">{selected.message || "-"}</pre></div><div><h3 className="detail-heading">结构化上下文</h3><pre className="code-block">{typeof selected.context === "string" ? selected.context : JSON.stringify(selected.context || {}, null, 2)}</pre></div></div>}
      </Modal>
    </div>
  );
}
